import { domains } from './cfqData';

export const calculateScores = (responses) => {
  const domainScores = domains.map((domain, domainIndex) => {
    let total = 0;
    for (let i = 0; i < domain.statements.length; i++) {
      total += responses[`${domainIndex}-${i}`] || 0;
    }
    return total;
  });
  const totalScore = domainScores.reduce((sum, score) => sum + score, 0);
  return { domainScores, totalScore };
};

export const getInterpretation = (score) => {
  if (score >= 80) return {
    insight: 'Your profile reflects a natural ability to move fluidly between cultural contexts while maintaining a clear sense of self.',
    reflection: 'You demonstrate consistent groundedness alongside genuine openness. Continue refining how you create space for others to do the same.'
  };
  if (score >= 60) return {
    insight: 'Your profile suggests meaningful cultural awareness, with room to deepen consistency across high-pressure or unfamiliar situations.',
    reflection: 'You navigate difference with intention. Consider where assumptions may still shape your responses before curiosity does.'
  };
  if (score >= 40) return {
    insight: 'Your profile indicates developing awareness of cultural dynamics, with opportunities to strengthen adaptive capacity.',
    reflection: 'Growth often begins with noticing patterns. Pay attention to moments where clarity or connection feels harder to reach.'
  };
  return {
    insight: 'Your profile highlights areas where cultural navigation may feel effortful or inconsistent, especially under pressure.',
    reflection: 'This is a starting point, not a verdict. Reflection and intentional practice can shift these patterns meaningfully over time.'
  };
};

export const getDomainInsight = (domainIndex, score) => {
  const insights = {
    0: {
      high: 'You appear grounded in your values while remaining open to adaptation. This foundation supports authentic engagement across contexts.',
      mid: 'You show awareness of your core identity, though maintaining groundedness may require more intention in unfamiliar settings.',
      low: 'Cultural environments may sometimes destabilise your sense of self. Clarifying non-negotiable values could strengthen this anchor.'
    },
    1: {
      high: 'You demonstrate capacity to suspend judgement and interpret behaviour through multiple cultural lenses.',
      mid: 'You show curiosity about different perspectives, though assumptions may still precede understanding at times.',
      low: 'Interpreting unfamiliar behaviour through your own lens may be limiting insight. Practice pausing before concluding.'
    },
    2: {
      high: 'You appear able to adjust expression while preserving clarity and voice. Meaning lands because delivery adapts.',
      mid: 'You show communication awareness, though adjustments may not always match the context or audience.',
      low: 'Communication may sometimes miss its mark across cultures. Notice where style overshadows substance.'
    },
    3: {
      high: 'You create conditions where trust can form despite difference. Shared space emerges through deliberate action.',
      mid: 'You build relationships across cultures, though deeper trust may require more consistent investment.',
      low: 'Building trust across cultural divides may feel challenging. Start with smaller, consistent gestures of inclusion.'
    }
  };
  if (score >= 20) return insights[domainIndex].high;
  if (score >= 12) return insights[domainIndex].mid;
  return insights[domainIndex].low;
};

export const getBandLabel = (score, max = 25) => {
  const pct = score / max;
  if (pct < 0.32) return 'Developing';
  if (pct < 0.6) return 'Emerging';
  if (pct < 0.8) return 'Established';
  return 'Advanced';
};

export const getNextStep = (domainIndex, band) => {
  const steps = {
    0: { Developing: 'Write down three values you will not compromise this month.', Emerging: 'Notice one moment this week where pressure shifts your behaviour.', Established: 'Help a colleague articulate their own anchoring values.', Advanced: 'Model grounded flexibility in your next high-stakes meeting.' },
    1: { Developing: 'Before reacting to unfamiliar behaviour, ask one "why" question.', Emerging: 'Seek out one perspective this week that challenges yours.', Established: 'Facilitate a conversation where two viewpoints are held equally.', Advanced: 'Coach someone to see a situation through a lens they resist.' },
    2: { Developing: 'Ask a trusted colleague how your directness lands with them.', Emerging: 'Adjust your tone in one conversation and observe the response.', Established: 'Translate a difficult message for two different audiences this week.', Advanced: 'Teach your team how to read unspoken cues in a meeting.' },
    3: { Developing: 'Invite someone from a different background for a genuine conversation.', Emerging: 'Create one moment of psychological safety in your next group setting.', Established: 'Bridge a visible gap between two people or groups this month.', Advanced: 'Design a practice that makes inclusion structural, not personal.' },
  };
  return steps[domainIndex]?.[band] || '';
};
